import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  Badge,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui";
import { useTenantUserSearch } from "~/features/admin/tenants/hooks";
import type { FilterUserOption, TenantOwner } from "~/features/admin/tenants/types";

interface ChangeOwnerDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  currentOwner: TenantOwner | null;
  onSelect: (user: FilterUserOption) => void;
}

/**
 * Search the organization's users and pick the single new owner. The actual
 * transfer is confirmed by the caller (AlertDialog) after selection.
 */
export function ChangeOwnerDialog({ open, onClose, organizationId, currentOwner, onSelect }: ChangeOwnerDialogProps) {
  const { t } = useTranslation();

  const { options, loading, search, reset } = useTenantUserSearch({
    organizationId,
    excludeIds: currentOwner ? [currentOwner.id] : [],
  });

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">{t("feature.tenants.changeOwner")}</DialogTitle>
        </DialogHeader>

        <Command shouldFilter={false} className="rounded-md border border-border-default">
          <CommandInput placeholder={t("ui.search")} onValueChange={search} />
          <CommandList>
            {!loading && <CommandEmpty>{t("feature.tenants.noUsersFound")}</CommandEmpty>}
            {options.map((option) => (
              <CommandItem key={option.id} value={option.id} onSelect={() => onSelect(option)}>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-fg-1">{option.displayName ?? option.email}</span>
                    {option.deactivatedAt && <Badge tone="default">{t("ui.deactivated")}</Badge>}
                  </span>
                  {option.displayName && <span className="truncate text-12-regular text-fg-3">{option.email}</span>}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
